import { Badge, Button, Flex, Group, Stack, Text, ThemeIcon, Title, UnstyledButton } from "@mantine/core";
import { IConnectedIssueMinInfo, IIssueApi } from "@schemas/issueSchema";
import { IconArrowDown, IconArrowUp, IconPlus } from "@tabler/icons-react";
import React from "react";
import {
  useGetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsQuery,
  useGetProjectByProjectIdIssuesTrpcIssueGetConnectedIssuesQuery,
} from "../../../../../../../openapi";
import { ISSUE_STATES_OPTIONS } from "../../../issueConstants";
import { addAppModalThunk } from "../../../../../../global/globalSlice";
import { useAppDispatch } from "../../../../../../hooks";
import { AppModalEnums } from "../../../../../../mc-shared/sharedConstants";
import FailedAlert from "../../../../../../components/FailedAlert";
import { ConnectIssueModal } from "./IssueConnectIssueModal";

// ---- Types that mirror your Zod schemas (for the UI layer) ----
export type IssueStatus = "Fullført" | "I prosess" | "På hold" | "Opprettet" | "Utgår";

function StatusDot({ status, size = 10 }: { status: IssueStatus; size?: number }) {
  const { color } = ISSUE_STATES_OPTIONS.find((s) => s.value === status) ?? { color: "gray" };
  return (
    <ThemeIcon size={size} radius="xl" color={color} variant="filled" style={{ minWidth: size, minHeight: size }} />
  );
}

function ConnectedRow({ item, onSelect }: { item: IConnectedIssueMinInfo; onSelect?: (id: string) => void }) {
  const registerName = item.issueBoard?.name;
  const content = (
    <Group justify="space-between" wrap="nowrap" w="100%">
      <Group gap={4} wrap="nowrap" justify="flex-start" align="center" miw={0} flex={1}>
        <StatusDot status={item.status} />
        <Text size="xs" c="dimmed" fw={700} style={{ flexShrink: 0, width: "3.5rem" }}>
          {item.id}
        </Text>
        <Text size="sm" fw={500} lineClamp={1} style={{ minWidth: 0 }}>
          {item.title}
        </Text>
      </Group>
      {registerName != null && (
        <Badge variant="light" color="gray" radius="sm" size="sm" tt="none" style={{ maxWidth: "12rem", flexShrink: 0 }}>
          <Text component="span" size="xs" truncate>
            {registerName}
          </Text>
        </Badge>
      )}
    </Group>
  );

  return (
    <UnstyledButton onClick={() => onSelect?.(item._id)} aria-label={`Open ${item.title}`} w="100%" p="xs">
      {content}
    </UnstyledButton>
  );
}

function ConnectionSection({
  title,
  icon,
  items,
  onSelect,
}: {
  title: string;
  icon: React.ReactNode;
  items: IConnectedIssueMinInfo[];
  onSelect?: (id: string) => void;
}) {
  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Group gap="xs">
          {icon}
          <Title order={6}>{title}</Title>
        </Group>
      </Group>

      <Stack gap={0}>
        {items.map((it) => (
          <ConnectedRow key={it._id} item={it} onSelect={onSelect} />
        ))}
      </Stack>
    </Stack>
  );
}

// ---- Main component (vertical: parents above, children below; only render sections with items) ----
export type IssueConnectionsProps = {
  issue: IIssueApi;
  boardId: string;
  projectId: string;
};

export function IssueConnectedIssuesSection({ issue, boardId, projectId }: IssueConnectionsProps) {
  const dispatch = useAppDispatch();
  const [connectIssueModalOpened, setConnectIssueModalOpened] = React.useState(false);

  const { data, isError } = useGetProjectByProjectIdIssuesTrpcIssueGetConnectedIssuesQuery({
    projectId,
    _id: issue._id,
  });

  const {
    data: otherBoardsData,
    isFetching: isOtherBoardsFetching,
    isError: isOtherBoardsError,
  } = useGetProjectByProjectIdIssuesByConnectedTasksAndIssuesFromOtherBoardsQuery({
    issueId: issue._id,
    projectId,
  });

  const { parents, children } = data ?? { parents: [], children: [] };
  const connectedFromOtherBoards =
    otherBoardsData?.map((c) => {
      return {
        _id: c._id,
        id: c.id,
        title: c.title,
        status: c.status,
        issueBoard: c.issueBoard,
      };
    }) ?? [];

  const childrenAndOtherBoards = React.useMemo(() => {
    const itemsById = new Map<string, IConnectedIssueMinInfo>();
    children.forEach((child) => itemsById.set(child._id, child));
    connectedFromOtherBoards.forEach((connectedIssue) => itemsById.set(connectedIssue._id, connectedIssue));
    return Array.from(itemsById.values());
  }, [children, connectedFromOtherBoards]);

  const hasParents = Array.isArray(parents) && parents.length > 0;
  const hasChildren = childrenAndOtherBoards.length > 0;

  const openIssue = (item: IConnectedIssueMinInfo) =>
    dispatch(
      addAppModalThunk({
        itemId: item._id,
        boardId: item.issueBoard?._id ?? boardId,
        projectId,
        app: AppModalEnums.ISSUE,
      }),
    );

  return (
    <Stack gap="sm">
      {isError === true && <FailedAlert errorMessage={<>Feil ved lasting av tilknyttede saker</>} />}
      {hasParents !== false && (
        <ConnectionSection
          // Wrong title due to data structure mistake
          title="Koblet til"
          icon={
            <ThemeIcon radius="xl" variant="light" color="cyan">
              <IconArrowUp size={16} />
            </ThemeIcon>
          }
          items={parents}
          onSelect={(id) => {
            const item = parents.find((parent) => parent._id === id);
            if (item != null) openIssue(item);
          }}
        />
      )}

      {hasChildren !== false && (
        <ConnectionSection
          // Wrong title due to data structure mistake
          title="Koblet fra"
          icon={
            <ThemeIcon size={26} radius="xl" variant="light" color="teal">
              <IconArrowDown size={16} />
            </ThemeIcon>
          }
          items={childrenAndOtherBoards}
          onSelect={(id) => {
            const item = childrenAndOtherBoards.find((child) => child._id === id);
            if (item != null) openIssue(item);
          }}
        />
      )}

      {isOtherBoardsError === true && (
        <FailedAlert errorMessage={<>Feil ved lasting av tilkoblinger fra andre registre</>} />
      )}
      <Flex w="100%" justify="flex-end">
        <Button variant="outline" onClick={() => setConnectIssueModalOpened(true)} leftSection={<IconPlus size={16} />}>
          Koble sak
        </Button>
      </Flex>
      <ConnectIssueModal
        projectId={projectId}
        boardId={issue?.issueBoard?._id}
        sourceIssueId={issue._id}
        opened={connectIssueModalOpened}
        onClose={() => setConnectIssueModalOpened(false)}
        blockedIssueIds={[...parents.map((item) => item._id), ...childrenAndOtherBoards.map((item) => item._id)]}
      />
    </Stack>
  );
}
